export interface RawSpace {
  space_name?: string;
  metadata?: string | Record<string, any>;
  region_name?: string;
  domain_name?: string;
  created_at?: string;
  bucket_name?: string;
  requested_for?: string;
  object_count?: number;
  sus_project_status?: string;
  failure_reason?: string;
  last_activity?: string;
  last_notebook_activity?: string;
  last_job_run?: string;
  last_job_run_feature_eng?: string;
  [key: string]: any;
}

export interface NormalizedSpace {
  create_error: string;
  spaceName: string;
  spaceNameMasked?: string;
  regionName: string;
  projectType: string;
  profileType: string;
  gxpFlag: string;
  objectCount: number;
  expiration: string;
  domainName: string;
  createdAt: string;
  bucketName: string;
  requestedFor: string;
  metaDataUsers: string;
  sus_git_repo: string;
  sus_project_id: string;
  sus_project_link: string;
  sus_project_metadata: string;
  data_account_id: string;
  is_active?: number;
  file_system_type?: string;
  sus_project_status?: string;
  failure_reason?: string;
  last_activity?: string;
  last_notebook_activity?: string;
  last_job_run?: string;
  last_job_run_feature_eng?: string;
  byoc_app_status?: string;
  byoc_inactive_reason?: string;
  byoc_app_last_update?: string;
}

export const PROFILE_TYPE_LABELS: Record<string, string> = {
  store_explore: "Store & Explore",
  data_product_eng: "Data Product Eng",
  store_analyze: "Store & Analyze",
  analytics_hpc: "Analytics on HPC",
  ml_feature_eng: "ML Feature Eng",
  gen_ai_eng: "Gen AI Eng",
  ml_model_eng: "ML Model Eng",
  advanced_fm: "Advanced FM on Hyperpod",
  "app_deployment_(BYOC)": "App Deployment (BYOC)",
};

// Profile type keys excluded from filter options (still displayed for existing spaces)
export const EXCLUDED_FILTER_PROFILE_TYPES: string[] = ["advanced_fm"];


export function normalizeSpaces(rawSpaces: RawSpace[]): NormalizedSpace[] {
  return rawSpaces.map((space) => {

    let metadata: any = {};
    if (typeof space.metadata === "string") {
      try { metadata = JSON.parse(space.metadata); } catch { metadata = {}; }
    } else if (typeof space.metadata === "object" && space.metadata !== null) {
      metadata = space.metadata;
    }

    // --- EXPIRY LOGIC ---
    let expiresOn = "–";
    const retentionType = metadata.retention_period_type;
    const retentionValue = metadata.retention_period_value;
    let createdAt = space.created_at || metadata.created_at || metadata["created-on"];


    if (createdAt && createdAt.includes(":")) {
      createdAt = createdAt.replace(/^(\d{4}-\d{2}-\d{2}):/, "$1T");
    }


    if (
      retentionType === "date" &&
      retentionValue &&
      retentionValue.length === 10 &&
      /^\d{4}-\d{2}-\d{2}$/.test(retentionValue)
    ) {
      expiresOn = retentionValue;
    } else if (retentionValue === "two_weeks") {
      // Calculate 14 days from creation date for experiment period
      if (createdAt) {
        const date = new Date(createdAt);
        if (!isNaN(date.getTime())) {
          date.setDate(date.getDate() + 14);
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");
          expiresOn = `${yyyy}-${mm}-${dd} (2 weeks)`;
        } else {
          expiresOn = "2 weeks";
        }
      } else {
        expiresOn = "2 weeks";
      }
    } else if (["short", "mid", "long", "gxp_long"].includes(retentionValue)) {
      const retentionYears: Record<string, number> = { short: 3, mid: 6, long: 9, gxp_long: 25 };
      const yearsToAdd = retentionYears[retentionValue] ?? 0;
      if (createdAt) {
        const date = new Date(createdAt);
        if (!isNaN(date.getTime())) {
          date.setFullYear(date.getFullYear() + yearsToAdd);
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");
          const displayValue = retentionValue === "gxp_long" ? "clinical study" : retentionValue;
          expiresOn = `${yyyy}-${mm}-${dd} (${displayValue})`;
        } else {
          expiresOn = retentionValue === "gxp_long" ? "clinical study" : retentionValue;
        }
      } else {
        expiresOn = retentionValue === "gxp_long" ? "clinical study" : retentionValue;
      }
    }    

    const normalized = {
      spaceName: space.space_name || metadata.name || "–",
      spaceNameMasked: space.space_name_mask || undefined,
      regionName: space.region_name || metadata["region-name"] || metadata.region_name || "–",
      projectType: metadata.project_type || "–",
      profileType: PROFILE_TYPE_LABELS[metadata.project_profile_type] || metadata.project_profile_type || "–",
      gxpFlag: metadata.bucket_type || "–",
      objectCount: space.object_count ?? 0,
      expiration: expiresOn,
      domainName: space.domain_name || metadata["primary-data-domain"] || "–",
      createdAt: space.created_at || metadata.created_at || "–",
      bucketName: space.bucket_name || "–",
      requestedFor: space.requested_for || metadata.requested_for || "–",
      metaDataUsers: metadata?.mapped_users,
      sus_git_repo: space?.sus_git_repo || "",
      sus_project_id: space?.sus_project_id || "",
      sus_project_link: space?.sus_project_link || "",
      sus_project_metadata: space?.sus_project_metadata || "",
      data_account_id: space?.data_account_id || "",
      is_active: space?.is_active !== undefined ? space.is_active : 1,
      create_error: space?.create_error || "",
      file_system_type: space?.file_system_type || metadata?.file_system_type || "",
      sus_project_status: space?.sus_project_status || "",
      failure_reason: space?.failure_reason || "",
      last_activity: space?.last_activity || "",
      last_notebook_activity: space?.last_notebook_activity || "",
      last_job_run: space?.last_job_run || "",
      last_job_run_feature_eng: space?.last_job_run_feature_eng || "",
      byoc_app_status: space?.byoc_app_status || "",
      byoc_inactive_reason: space?.byoc_inactive_reason || "",
      byoc_app_last_update: space?.byoc_app_last_update || "",
    };


    return normalized;
  });
}
